import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

export default class extends Controller {
  static targets = [
    "searchInput", "frame", "userCount", "exportButton",
    // "User permissions" (project access scope) modal
    "accessScopeModal", "accessScopeName", "accessScopeSaveBtn",
    // "Page access" (which pages a teammate can open) modal
    "pageAccessModal", "pageAccessName", "pageAccessUserId",
    "pageAccessCheckbox", "pageAccessError", "pageAccessSaveBtn",
    // "Info" (read-only card about a teammate) modal
    "infoModal", "infoLoading", "infoBody",
    // Add Users modal
    "addUsersModal", "addUsersRows", "addUsersError", "addUsersSaveBtn",
    // Default role settings modal
    "defaultRoleButton", "defaultRoleModal", "defaultRoleSelect", "defaultRoleSaveBtn", "defaultRoleError",
    // Restore user confirmation modal
    "restoreModal", "restoreEmail", "restoreRole",
    // Permissions modal
    "permissionsModal", "permUserId", "permUserName", "permUserEmail",
    "permAvatarImg", "permAvatarImage", "permAvatarInitials",
    "permFirstName", "permLastName", "permPermissions", "permRole",
    "regionSection", "regionSelect", "regionTags", "regionIds",
    "permSaveBtn", "permAdminAssistant", "permHidden", "permMute",
    "commanderOnlyOptions", "addCommanderOnlyOptions",
    "addHidden", "addMute", "confirmModal",
    // Switch Company + reassignment
    "switchCompanySection", "switchCompanySelect",
    "reassignmentModal", "reassignmentSubtitle", "reassignmentIntro", "reassignmentProjectList",
    "reassignmentEmpty", "reassignmentConfirmBtn",
    // Transfer ownership on delete
    "transferModal", "transferSubtitle", "transferUserSelect", "transferConfirmBtn",
    // Group Admin confirm modal
    "gaConfirmModal",
    // Removed users (trash)
    "removedUsersModal", "removedUsersEmpty", "removedUsersLoading",
    "removedUsersListSection", "removedUsersHistorySection", "removedUsersHistoryEmpty",
    "removedUsersHistoryLoading", "removedUsersHistoryToggle",
    // Associated companies
    "companySearch", "companySearchResults", "companySearchWrapper",
    "companySearchMode", "companyBrowsingMode", "browsingCompanyName",
    "addUsersButton"
  ]

  static values = {
    createUsersUrl: String,
    restoreUserUrl: String,
    resendInviteUrl: String,
    defaultRoleUrl: String,
    defaultRole: String,
    updatePermissionUrl: String,
    sectionAccessUrl: String,
    teamInfoUrl: String,
    previewCompanySwitchUrl: String,
    switchI18n: Object,
    destroyUserUrl: String,
    teamUrl: String,
    deletedUsersUrl: String,
    teamMemberHistoryUrl: String,
    isCommander: Boolean,
    isCaptain: Boolean,
    isGroupAdmin: Boolean,
    associatedCompaniesUrl: String,
    isClient: Boolean,
    currentUserCompanyId: Number,
    // Owner's Reps tab (Phelipe 2026-10-21): the same Info / Edit / Remove
    // pop-ups and trash can, pointed at the rep endpoints (REP_ID / USER_ID).
    ownerRepInfoUrl: String,
    ownerRepPermissionUrl: String,
    ownerRepDestroyUrl: String,
    ownerRepRemovedUrl: String,
    ownerRepRestoreUrl: String,
    ownerRepI18n: Object
  }

  connect() {
    this.searchTimeout = null
    this.companySearchTimeout = null
    this.selectedCompanyId = null
    this.associatedCompanies = null

    // Close company search results on outside click
    this._onDocClick = (e) => {
      if (this.hasCompanySearchResultsTarget && this.hasCompanySearchWrapperTarget && !this.companySearchWrapperTarget.contains(e.target)) {
        this.companySearchResultsTarget.classList.add("hidden")
      }
    }
    document.addEventListener("click", this._onDocClick)
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    clearTimeout(this.companySearchTimeout)
    document.removeEventListener("click", this._onDocClick)
  }

  // ── Search ──────────────────────────────────────────────────────────

  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this._reloadFrame(), 300)
  }

  // ── Add Users Modal ─────────────────────────────────────────────────

  openAddUsersModal() {
    this._resetAddUsersForm()
    this.addUsersModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeAddUsersModal() {
    this.addUsersModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._resetAddUsersForm()
  }

  addUserRow() {
    const container = this.addUsersRowsTarget
    const firstRow = container.querySelector("[data-add-user-row]")
    const clone = firstRow.cloneNode(true)

    // Clear values
    clone.querySelector('[data-field="email"]').value = ""
    clone.querySelector('[data-field="role"]').value = this._defaultRole()

    // Replace empty placeholder with remove button
    const actionDiv = clone.querySelector("[data-remove-area]")
    actionDiv.innerHTML = `
      <button type="button"
              class="text-red-500 hover:text-red-700 text-xs font-medium cursor-pointer transition-colors"
              data-action="click->team#removeUserRow">
        Remove
      </button>
    `

    container.appendChild(clone)
  }

  removeUserRow(event) {
    const row = event.currentTarget.closest("[data-add-user-row]")
    row.remove()
  }

  async submitAddUsers() {
    const rows = this.addUsersRowsTarget.querySelectorAll("[data-add-user-row]")
    const userData = []

    for (const row of rows) {
      const email = row.querySelector('[data-field="email"]').value.trim()
      const role = row.querySelector('[data-field="role"]').value
      if (email) userData.push([email, role])
    }

    if (userData.length === 0) return

    const btn = this.addUsersSaveBtnTarget
    btn.disabled = true
    btn.textContent = "Saving..."

    try {
      const resp = await fetch(this.createUsersUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({
          new_user_data: JSON.stringify(userData),
          hidden_from_teams: this.hasAddHiddenTarget ? this.addHiddenTarget.checked : undefined,
          mute_emails: this.hasAddMuteTarget ? this.addMuteTarget.checked : undefined
        })
      })

      const data = await this._readJson(resp)
      if (resp.ok && data) {
        this.closeAddUsersModal()
        this._reloadFrame()
        this._updateUserCount(userData.length)
        if (data.message) this._showToast(data.message)
      } else if (resp.status === 409 && data && data.restore_prompt) {
        this._pendingRestoreEmail = data.email
        this._pendingRestoreRole = data.role
        this.closeAddUsersModal()
        this._showRestoreModal(data.email)
      } else {
        this._showAddUsersError(this._addUsersFailure(resp, data))
      }
    } catch (e) {
      this._showAddUsersError("Couldn't reach ConsLog — check your connection and try again.")
    } finally {
      btn.disabled = false
      btn.textContent = "Save"
    }
  }

  // The answer as JSON, or null when it isn't JSON (a sign-in page, an
  // error page) — never throws.
  async _readJson(resp) {
    const type = resp.headers.get("content-type") || ""
    if (!type.includes("json")) return null
    return resp.json().catch(() => null)
  }

  // Turns a failed add-users answer into words (Colton, 2026-09-23): the
  // popup used to say a bare "Error adding users" for a timed-out login, a
  // permission refusal and a server crash alike, so nobody could tell what
  // to do next.
  _addUsersFailure(resp, data) {
    const status = resp.status
    if (status === 401 || (resp.ok && !data)) return "Your login timed out. Reload the page, sign in, and try again."
    if (data && data.error_message) return data.error_message
    if (status === 403) return "Only a team commander can add users here."
    if (status === 422) return "This page is out of date. Reload it and try again."
    if (status >= 500) return `Something went wrong on our side (error ${status}). Tell support the time it happened and we'll check the log.`
    return `Error adding users (${status}).`
  }

  _showAddUsersError(message) {
    const el = this.addUsersErrorTarget
    el.textContent = message
    el.classList.remove("hidden")
  }

  _resetAddUsersForm() {
    if (!this.hasAddUsersRowsTarget) return

    const container = this.addUsersRowsTarget
    const rows = container.querySelectorAll("[data-add-user-row]")

    // Keep only first row, remove the rest
    rows.forEach((row, i) => { if (i > 0) row.remove() })

    // Reset first row
    const first = container.querySelector("[data-add-user-row]")
    if (first) {
      first.querySelector('[data-field="email"]').value = ""
      first.querySelector('[data-field="role"]').value = this._defaultRole()
    }

    // Hide error
    if (this.hasAddUsersErrorTarget) {
      this.addUsersErrorTarget.classList.add("hidden")
      this.addUsersErrorTarget.textContent = ""
    }
  }

  _defaultRole() {
    return this.hasDefaultRoleValue && this.defaultRoleValue ? this.defaultRoleValue : "user"
  }

  // ── Default Role Settings Modal ─────────────────────────────────────

  openDefaultRoleModal() {
    if (this.hasDefaultRoleSelectTarget) this.defaultRoleSelectTarget.value = this._defaultRole()
    if (this.hasDefaultRoleErrorTarget) {
      this.defaultRoleErrorTarget.classList.add("hidden")
      this.defaultRoleErrorTarget.textContent = ""
    }
    this.defaultRoleModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeDefaultRoleModal() {
    this.defaultRoleModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  async submitDefaultRole() {
    const role = this.defaultRoleSelectTarget.value
    const btn = this.defaultRoleSaveBtnTarget
    btn.disabled = true
    btn.textContent = "Saving..."

    try {
      const resp = await fetch(this.defaultRoleUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ default_member_role: role })
      })

      if (resp.ok) {
        const data = await resp.json()
        this.defaultRoleValue = data.default_member_role || role
        this._applyDefaultRoleToAddUsers()
        this.closeDefaultRoleModal()
        if (data.message) this._showToast(data.message)
      } else {
        const data = await resp.json().catch(() => ({}))
        this._showDefaultRoleError(data.error_message || "Error saving default role")
      }
    } catch (e) {
      this._showDefaultRoleError("Error saving default role")
    } finally {
      btn.disabled = false
      btn.textContent = "Save"
    }
  }

  _showDefaultRoleError(message) {
    if (!this.hasDefaultRoleErrorTarget) return
    this.defaultRoleErrorTarget.textContent = message
    this.defaultRoleErrorTarget.classList.remove("hidden")
  }

  // Keep the (currently closed) Add Users dropdowns in sync with the new default.
  _applyDefaultRoleToAddUsers() {
    if (!this.hasAddUsersRowsTarget) return
    this.addUsersRowsTarget.querySelectorAll('[data-field="role"]').forEach((select) => {
      select.value = this._defaultRole()
    })
  }

  // ── Restore User Modal ──────────────────────────────────────────────

  _showRestoreModal(email) {
    this.restoreEmailTarget.textContent = email
    this.restoreModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeRestoreModal() {
    this.restoreModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._pendingRestoreEmail = null
    this._pendingRestoreRole = null
  }

  async confirmRestore() {
    const email = this._pendingRestoreEmail
    const role = this._pendingRestoreRole
    if (!email) return

    this.closeRestoreModal()

    try {
      const resp = await fetch(this.restoreUserUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ email, role })
      })

      if (resp.ok) {
        const data = await resp.json()
        this._reloadFrame()
        this._updateUserCount(1)
        this._showToast(data.message || "User restored successfully")
      } else {
        const data = await resp.json()
        this._showToast(data.error_message || "Error restoring user", "error")
      }
    } catch (e) {
      this._showToast("Error restoring user", "error")
    }
  }

  // ── Resend Invite ───────────────────────────────────────────────────

  async resendInvite(event) {
    const btn = event.currentTarget
    const userId = btn.dataset.userId
    const originalHTML = btn.innerHTML

    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`

    try {
      const resp = await fetch(this.resendInviteUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ user_id: userId })
      })
      if (!resp.ok) throw new Error("failed")

      btn.innerHTML = `<span class="text-green-600 font-medium">Sent!</span>`
      // The row menu closes itself on click, so say it somewhere that stays.
      this._showToast(`Sign-up email sent to ${btn.dataset.userEmail || "the user"}`)
      setTimeout(() => { btn.innerHTML = originalHTML; btn.disabled = false }, 2000)
    } catch (e) {
      btn.innerHTML = originalHTML
      btn.disabled = false
      this._showToast("Could not resend the invitation", "error")
    }
  }

  // ── Permissions Modal ───────────────────────────────────────────────

  openPermissionsModal(event) {
    const btn = event.currentTarget
    const d = btn.dataset
    this._ownerRepId = d.ownerRepId || null

    this.permUserIdTarget.value = d.userId
    this.permUserNameTarget.textContent = d.userName || d.userEmail
    this.permUserEmailTarget.textContent = d.userEmail

    // Avatar
    const initials = d.userInitials || ""
    if (d.userAvatar) {
      this.permAvatarImgTarget.classList.remove("hidden")
      this.permAvatarImageTarget.src = d.userAvatar
      this.permAvatarInitialsTarget.classList.add("hidden")
      this.permAvatarImageTarget.onerror = () => {
        this.permAvatarImgTarget.classList.add("hidden")
        this.permAvatarInitialsTarget.classList.remove("hidden")
        this.permAvatarInitialsTarget.textContent = initials
      }
    } else {
      this.permAvatarImgTarget.classList.add("hidden")
      this.permAvatarInitialsTarget.classList.remove("hidden")
      this.permAvatarInitialsTarget.textContent = initials
    }

    this.permFirstNameTarget.value = d.userFirstName || ""
    this.permLastNameTarget.value = d.userLastName || ""
    this.permPermissionsTarget.value = d.userReadOnly === "true" ? "view_only" : "edit"
    this.permRoleTarget.value = d.userRole || "user"

    // Owner-module team options (commander-only section; absent for builder companies)
    if (this.hasPermHiddenTarget) this.permHiddenTarget.checked = d.userHidden === "true"
    if (this.hasPermMuteTarget) this.permMuteTarget.checked = d.userMuted === "true"
    this._toggleCommanderOnlyOptions()

    if (this.hasPermAdminAssistantTarget) {
      this.permAdminAssistantTarget.checked = d.userAdminAssistant === "true"
    }

    // Regions
    this._clearRegionTags()
    this.regionIdsTarget.value = d.userRegionIds || ""

    if (d.userRegionIds) {
      const ids = d.userRegionIds.split(",").filter(Boolean)
      ids.forEach(id => {
        const option = this.regionSelectTarget.querySelector(`option[value="${id}"]`)
        if (option) {
          this._addRegionTag(id, option.dataset.name || option.textContent)
        }
      })
    }

    this._toggleRegionSection()
    this._resetSwitchCompanySection(d.userCompanyId)
    this._paintOwnerRepMode()
    this.permissionsModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  _resetSwitchCompanySection(editedUserCompanyId) {
    if (!this.hasSwitchCompanySectionTarget) return

    const sameCompany = Number(editedUserCompanyId) === Number(this.currentUserCompanyIdValue)
    this.switchCompanySectionTarget.classList.toggle("hidden", !sameCompany)
    if (this.hasSwitchCompanySelectTarget) this.switchCompanySelectTarget.value = ""
  }

  closePermissionsModal() {
    // The permissions modal only renders for commanders/captains — a
    // commander-less-company member removing a teammate (Phelipe
    // 2026-08-19) reaches this in _performDelete's success path without
    // the modal on the page. Guard, or the success flow throws and the
    // user sees "Error removing user" for a removal that worked.
    if (!this.hasPermissionsModalTarget) return
    this.permissionsModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  onRoleChange() {
    this._toggleRegionSection()
    this._toggleCommanderOnlyOptions()
  }

  // Hide/Mute apply to the company-wide admin roles — commanders AND
  // captains (Phelipe 2026-08-20). Clearing the boxes when the role moves
  // elsewhere keeps the saved state honest — the server does the same.
  _toggleCommanderOnlyOptions() {
    if (!this.hasCommanderOnlyOptionsTarget) return

    const isCompanyAdmin = ["commander", "captain"].includes(this.permRoleTarget.value)
    this.commanderOnlyOptionsTarget.classList.toggle("hidden", !isCompanyAdmin)
    if (!isCompanyAdmin) {
      if (this.hasPermHiddenTarget) this.permHiddenTarget.checked = false
      if (this.hasPermMuteTarget) this.permMuteTarget.checked = false
    }
  }

  // Same rule on the Add Users batch: the section only appears once a row is
  // being added as a commander or captain.
  onAddUserRoleChange() {
    if (!this.hasAddCommanderOnlyOptionsTarget) return

    const roles = [...this.addUsersRowsTarget.querySelectorAll('[data-field="role"]')].map((el) => el.value)
    const anyCompanyAdmin = roles.some((r) => r === "commander" || r === "captain")
    this.addCommanderOnlyOptionsTarget.classList.toggle("hidden", !anyCompanyAdmin)
    if (!anyCompanyAdmin) {
      if (this.hasAddHiddenTarget) this.addHiddenTarget.checked = false
      if (this.hasAddMuteTarget) this.addMuteTarget.checked = false
    }
  }

  addRegion() {
    const select = this.regionSelectTarget
    const id = select.value
    if (!id) return

    const name = select.options[select.selectedIndex].dataset.name || select.options[select.selectedIndex].text

    // Check if already added
    const current = this.regionIdsTarget.value.split(",").filter(Boolean)
    if (current.includes(id)) {
      select.value = ""
      return
    }

    current.push(id)
    this.regionIdsTarget.value = current.join(",")
    this._addRegionTag(id, name)
    select.value = ""
  }

  removeRegion(event) {
    const tag = event.currentTarget.closest("[data-region-tag]")
    const id = tag.dataset.regionId

    const current = this.regionIdsTarget.value.split(",").filter(Boolean)
    this.regionIdsTarget.value = current.filter(r => r !== id).join(",")
    tag.remove()
  }

  removeUser() {
    this._setConfirmText(this._ownerRepId ? this._repRemoveText(this.permUserNameTarget.textContent) : null)
    this.confirmModalTarget.classList.remove("hidden")
  }

  // Owner's Reps tab menu → Remove user: the same confirmation pop-up.
  removeOwnerRep(event) {
    const d = event.currentTarget.dataset
    this._ownerRepId = d.ownerRepId
    this.permUserIdTarget.value = d.userId
    this._setConfirmText(this._repRemoveText(d.userName))
    this.confirmModalTarget.classList.remove("hidden")
  }

  cancelRemoveUser() {
    this.confirmModalTarget.classList.add("hidden")
  }

  async confirmRemoveUser() {
    this.confirmModalTarget.classList.add("hidden")
    await this._destroyUser(this.permUserIdTarget.value)
  }

  async submitPermissions() {
    const switchTargetId = this.hasSwitchCompanySelectTarget ? this.switchCompanySelectTarget.value : ""

    if (switchTargetId) {
      await this._startCompanySwitch(switchTargetId)
    } else {
      await this._submitPermissionsRequest({})
    }
  }

  async _startCompanySwitch(switchTargetId) {
    const btn = this.permSaveBtnTarget
    btn.disabled = true
    btn.textContent = "Checking projects..."

    try {
      const userId = this.permUserIdTarget.value
      const url = `${this.previewCompanySwitchUrlValue.replace(":id", userId)}?target_company_id=${switchTargetId}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })

      if (!resp.ok) {
        this._showToast("Unable to check project ownership", "error")
        return
      }

      const data = await resp.json()
      // Always confirm first: the move also takes the person off this
      // account's projects, so say so even when nothing needs handing off.
      this._openReassignmentModal(data, switchTargetId)
    } catch (e) {
      this._showToast("Unable to check project ownership", "error")
    } finally {
      btn.disabled = false
      btn.textContent = "Save Changes"
    }
  }

  async _submitPermissionsRequest({ switchTargetId, projectDecisions } = {}) {
    const userId = this.permUserIdTarget.value
    const btn = this.permSaveBtnTarget
    btn.disabled = true
    btn.textContent = "Saving..."

    const url = this._ownerRepId
      ? this._repUrl(this.ownerRepPermissionUrlValue, this._ownerRepId, userId)
      : this.updatePermissionUrlValue.replace(":id", userId)
    const body = {
      user_role: this.permRoleTarget.value,
      user_permissions: this.permPermissionsTarget.value,
      first_name: this.permFirstNameTarget.value.trim(),
      last_name: this.permLastNameTarget.value.trim(),
      region_ids: this.regionIdsTarget.value,
      admin_assistant: this.hasPermAdminAssistantTarget ? this.permAdminAssistantTarget.checked : undefined,
      hidden_from_teams: this.hasPermHiddenTarget ? this.permHiddenTarget.checked : undefined,
      mute_emails: this.hasPermMuteTarget ? this.permMuteTarget.checked : undefined
    }
    if (switchTargetId) body.switch_account = switchTargetId
    if (projectDecisions) body.project_decisions = projectDecisions

    try {
      const resp = await fetch(url, {
        method: this._ownerRepId ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify(body)
      })

      if (resp.ok) {
        this._closeReassignmentModal()
        this.closePermissionsModal()
        this._ownerRepId ? this._reloadOwnerRepsFrame() : this._reloadFrame()
        this._showToast("User updated successfully")
      } else {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.errors ? data.errors.join(", ") : "Error updating permissions", "error")
      }
    } catch (e) {
      this._showToast("Error updating permissions", "error")
    } finally {
      btn.disabled = false
      btn.textContent = "Save Changes"
    }
  }

  // ── Reassignment Modal ──────────────────────────────────────────────

  _openReassignmentModal(data, switchTargetId) {
    if (!this.hasReassignmentModalTarget) return

    this._pendingSwitchTargetId = switchTargetId
    const projects = data.projects || []
    const userName = (this.permUserNameTarget.textContent || "").trim() || "this user"
    const company = data.new_company || ""
    const i18n = this.switchI18nValue || {}
    const fill = (tpl, vars) => (tpl || "").replace(/%\{(\w+)\}/g, (_, k) => (vars[k] ?? ""))

    if (this.hasReassignmentSubtitleTarget) {
      const n = data.leaving_count || 0
      const tpl = n === 0 ? i18n.leaving_none : (n === 1 ? i18n.leaving_one : i18n.leaving_other)
      this.reassignmentSubtitleTarget.textContent = fill(tpl, { user: userName, company, count: n })
    }
    if (this.hasReassignmentIntroTarget) {
      this.reassignmentIntroTarget.textContent = fill(i18n.intro, { user: userName })
      this.reassignmentIntroTarget.classList.toggle("hidden", projects.length === 0)
    }
    if (this.hasReassignmentEmptyTarget) {
      this.reassignmentEmptyTarget.classList.toggle("hidden", projects.length > 0)
    }

    const list = this.reassignmentProjectListTarget
    list.innerHTML = ""
    projects.forEach((project) => {
      const row = document.createElement("div")
      row.className = "flex items-center justify-between gap-4 p-3 rounded-lg border border-gray-200 bg-white"
      row.setAttribute("data-project-row", "")
      row.setAttribute("data-project-id", project.id)

      const left = document.createElement("div")
      left.className = "min-w-0 flex-1"
      const title = document.createElement("div")
      title.className = "text-sm font-medium text-gray-900 truncate"
      title.textContent = project.name
      left.appendChild(title)

      const select = document.createElement("select")
      select.className = "shrink-0 w-96 rounded-lg border border-gray-300 px-2 py-1.5 text-[13px] focus:outline-none focus:border-brand bg-white"
      select.setAttribute("data-project-decision", "")

      const keepOpt = document.createElement("option")
      keepOpt.value = "keep"
      keepOpt.textContent = fill(i18n.keep, { user: userName, company })
      select.appendChild(keepOpt)

      ;(project.candidates || []).forEach((c) => {
        const opt = document.createElement("option")
        opt.value = String(c.id)
        opt.textContent = fill(i18n.replace, { name: c.name })
        select.appendChild(opt)
      })

      row.appendChild(left)
      row.appendChild(select)
      list.appendChild(row)
    })

    this.reassignmentModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  _closeReassignmentModal() {
    if (!this.hasReassignmentModalTarget) return
    this.reassignmentModalTarget.classList.add("hidden")
    this._pendingSwitchTargetId = null
  }

  closeReassignmentModal() {
    this._closeReassignmentModal()
    document.body.style.overflow = "hidden"
  }

  async confirmReassignment() {
    if (!this._pendingSwitchTargetId) return

    const decisions = {}
    this.reassignmentProjectListTarget.querySelectorAll("[data-project-row]").forEach((row) => {
      const pid = row.dataset.projectId
      const val = row.querySelector("[data-project-decision]").value
      decisions[pid] = val
    })

    const btn = this.reassignmentConfirmBtnTarget
    btn.disabled = true
    btn.textContent = "Applying..."

    await this._submitPermissionsRequest({
      switchTargetId: this._pendingSwitchTargetId,
      projectDecisions: decisions
    })

    btn.disabled = false
    btn.textContent = "Confirm switch"
  }

  async _destroyUser(userId) {
    const btn = this.permSaveBtnTarget
    btn.disabled = true
    btn.textContent = "Removing..."

    try {
      await this._performDelete(userId)
    } finally {
      btn.disabled = false
      btn.textContent = "Save Changes"
    }
  }

  async _performDelete(userId, transferToUserId = null) {
    const repId = this._ownerRepId
    const url = repId ? this._repUrl(this.ownerRepDestroyUrlValue, repId, userId) : this.destroyUserUrlValue.replace(":id", userId)
    const init = {
      method: "DELETE",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this._csrfToken()
      }
    }
    if (transferToUserId) {
      init.headers["Content-Type"] = "application/json"
      init.body = JSON.stringify({ transfer_to_user_id: transferToUserId })
    }

    try {
      const resp = await fetch(url, init)

      if (resp.ok) {
        this._closeTransferModal()
        this.closePermissionsModal()
        if (repId) {
          const data = await resp.json().catch(() => ({}))
          this._reloadOwnerRepsFrame()
          this._showToast(data.message || "User removed successfully")
          return { ok: true }
        }
        this._reloadFrame()
        this._updateUserCount(-1)
        this._showToast(transferToUserId ? "Ownership transferred — user removed" : "User removed successfully")
        return { ok: true }
      }

      if (resp.status === 422) {
        const data = await resp.json().catch(() => ({}))
        if (data.needs_transfer) {
          this._openTransferModal(userId, data)
          return { ok: false, needsTransfer: true }
        }
        this._showToast(data.errors ? data.errors.join(", ") : "Error removing user", "error")
        return { ok: false }
      }

      this._showToast("Error removing user", "error")
      return { ok: false }
    } catch (e) {
      this._showToast("Error removing user", "error")
      return { ok: false }
    }
  }

  // ── Transfer Ownership Modal ────────────────────────────────────────

  _openTransferModal(userId, data) {
    if (!this.hasTransferModalTarget) return

    this._pendingDeleteUserId = userId
    const permName = this.hasPermUserNameTarget ? this.permUserNameTarget.textContent : ""
    const userName = permName || this._gaRemoveUserName || "this user"
    const count = data.project_count || 0

    if (this.hasTransferSubtitleTarget) {
      this.transferSubtitleTarget.textContent =
        `${userName} owns ${count} project${count === 1 ? "" : "s"}. Pick a teammate to inherit them.`
    }

    const select = this.transferUserSelectTarget
    select.innerHTML = '<option value="">Select a teammate...</option>'
    ;(data.candidates || []).forEach((c) => {
      const opt = document.createElement("option")
      opt.value = String(c.id)
      opt.textContent = c.name
      select.appendChild(opt)
    })
    select.value = ""
    if (this.hasTransferConfirmBtnTarget) this.transferConfirmBtnTarget.disabled = true

    // Hide any lingering delete confirm layers so transfer is the only visible modal
    if (this.hasConfirmModalTarget) this.confirmModalTarget.classList.add("hidden")
    if (this.hasGaConfirmModalTarget) this.gaConfirmModalTarget.classList.add("hidden")
    if (this.hasPermissionsModalTarget) this.permissionsModalTarget.classList.add("hidden")

    if (!this.hasTransferModalTarget) {
      this._showToast("This user owns projects — reload the page and try again to pick a replacement owner.", "error")
      return
    }
    this.transferModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  _closeTransferModal() {
    if (!this.hasTransferModalTarget) return
    this.transferModalTarget.classList.add("hidden")
    this._pendingDeleteUserId = null
  }

  closeTransferModal() {
    this._closeTransferModal()
    document.body.style.overflow = ""
  }

  onTransferSelectChange() {
    if (!this.hasTransferConfirmBtnTarget) return
    this.transferConfirmBtnTarget.disabled = !this.transferUserSelectTarget.value
  }

  async confirmTransfer() {
    if (!this._pendingDeleteUserId) return
    const replacementId = this.transferUserSelectTarget.value
    if (!replacementId) return

    const btn = this.transferConfirmBtnTarget
    btn.disabled = true
    btn.textContent = "Transferring..."

    await this._performDelete(this._pendingDeleteUserId, replacementId)

    btn.disabled = false
    btn.textContent = "Transfer and remove"
  }

  _toggleRegionSection() {
    if (!this.hasRegionSectionTarget) return
    const isRegional = this.permRoleTarget.value === "regional_access"
    this.regionSectionTarget.classList.toggle("hidden", !isRegional)
  }

  _addRegionTag(id, name) {
    const tag = document.createElement("span")
    tag.setAttribute("data-region-tag", "")
    tag.setAttribute("data-region-id", id)
    tag.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand/10 text-brand"
    tag.innerHTML = `
      ${this._escapeHtml(name)}
      <button type="button" class="hover:text-red-600 transition-colors cursor-pointer" data-action="click->team#removeRegion">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
    `
    this.regionTagsTarget.appendChild(tag)
  }

  _clearRegionTags() {
    if (this.hasRegionTagsTarget) this.regionTagsTarget.innerHTML = ""
    if (this.hasRegionIdsTarget) this.regionIdsTarget.value = ""
  }

  // ── Group Admin Direct Remove ────────────────────────────────────────

  confirmRemoveDirectly(event) {
    this._ownerRepId = null
    const btn = event.currentTarget
    this._gaRemoveUserId = btn.dataset.userId
    this._gaRemoveUserName = btn.dataset.userName

    if (this.hasGaConfirmModalTarget) {
      const msg = this.gaConfirmModalTarget.querySelector("p")
      if (msg) msg.textContent = `This will remove ${this._gaRemoveUserName} from the company and all associated projects.`
      this.gaConfirmModalTarget.classList.remove("hidden")
    }
  }

  cancelGaRemove() {
    if (this.hasGaConfirmModalTarget) this.gaConfirmModalTarget.classList.add("hidden")
    this._gaRemoveUserId = null
    this._gaRemoveUserName = null
  }

  async confirmGaRemove() {
    if (this.hasGaConfirmModalTarget) this.gaConfirmModalTarget.classList.add("hidden")
    if (!this._gaRemoveUserId) return

    const result = await this._performDelete(this._gaRemoveUserId)

    // If a transfer modal opened, keep the user/name so its subtitle can reference them.
    if (!result.needsTransfer) {
      this._gaRemoveUserId = null
      this._gaRemoveUserName = null
    }
  }

  // ── Associated Companies Search ─────────────────────────────────────

  async searchCompany() {
    clearTimeout(this.companySearchTimeout)
    const query = this.companySearchTarget.value.trim()

    if (query.length < 3) {
      this.companySearchResultsTarget.classList.add("hidden")
      return
    }

    this.companySearchTimeout = setTimeout(async () => {
      // Lazy-load the full list once
      if (!this.associatedCompanies) {
        try {
          const resp = await fetch(this.associatedCompaniesUrlValue, {
            headers: { "Accept": "application/json" }
          })
          this.associatedCompanies = await resp.json()
        } catch {
          this.associatedCompanies = []
        }
      }

      const lower = query.toLowerCase()
      const matches = this.associatedCompanies.filter(c => c.name.toLowerCase().includes(lower))
      const results = this.companySearchResultsTarget

      if (matches.length === 0) {
        results.classList.add("hidden")
        return
      }

      results.innerHTML = matches.slice(0, 15).map(c =>
        `<button type="button"
                 class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer truncate"
                 data-action="click->team#selectCompany"
                 data-company-id="${c.id}"
                 data-company-name="${this._escapeHtml(c.name)}">${this._escapeHtml(c.name)}</button>`
      ).join("")
      results.classList.remove("hidden")
    }, 300)
  }

  selectCompany(event) {
    const btn = event.currentTarget
    this.selectedCompanyId = btn.dataset.companyId
    const name = btn.dataset.companyName

    // Update UI: swap search input for browsing pill
    this.companySearchResultsTarget.classList.add("hidden")
    this.companySearchTarget.value = ""
    this.companySearchModeTarget.classList.add("hidden")
    this.browsingCompanyNameTarget.textContent = name
    this.companyBrowsingModeTarget.classList.remove("hidden")
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._setToolbarDisabled(true)

    this._reloadFrame()
  }

  backToMyTeam() {
    this.selectedCompanyId = null
    this.companyBrowsingModeTarget.classList.add("hidden")
    this.companySearchModeTarget.classList.remove("hidden")
    this._setToolbarDisabled(false)
    this._reloadFrame()
  }

  // ── Export ──────────────────────────────────────────────────────────

  exportCsv() {
    // Close dropdown
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    this._showExportSpinner()

    const search = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    const params = new URLSearchParams()
    params.set("format", "csv")
    if (search) params.set("term", search)

    const url = `${this.teamUrlValue}?${params.toString()}`

    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        const today = new Date().toISOString().split("T")[0]
        a.download = `team-${today}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(blobUrl)
      })
      .catch(() => alert("Error exporting CSV"))
      .finally(() => this._hideExportSpinner())
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  // ── Backdrop close ──────────────────────────────────────────────────

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      event.currentTarget.classList.add("hidden")
    }
  }

  // ── Internals ───────────────────────────────────────────────────────

  _setToolbarDisabled(disabled) {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.disabled = disabled
      this.searchInputTarget.classList.toggle("opacity-40", disabled)
    }
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.disabled = disabled
      this.exportButtonTarget.classList.toggle("opacity-40", disabled)
      this.exportButtonTarget.classList.toggle("pointer-events-none", disabled)
    }
    if (this.hasAddUsersButtonTarget) {
      this.addUsersButtonTarget.disabled = disabled
      this.addUsersButtonTarget.classList.toggle("opacity-40", disabled)
      this.addUsersButtonTarget.classList.toggle("pointer-events-none", disabled)
    }
  }

  _reloadFrame() {
    const frame = this.frameTarget
    const search = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""

    const params = new URLSearchParams()
    params.set("format", "html")
    if (search) params.set("term", search)
    if (this.selectedCompanyId) params.set("company_id", this.selectedCompanyId)

    frame.src = `${this.teamUrlValue}?${params.toString()}`
  }

  // ── Owner's Reps tab helpers ────────────────────────────────────────

  _repUrl(template, repId, userId) {
    return template.replace("REP_ID", repId).replace("USER_ID", userId)
  }

  _ownerRepsTabActive() {
    const panel = document.querySelector('[data-team-tab-panel="reps"]')
    return !!panel && !panel.classList.contains("hidden")
  }

  _reloadOwnerRepsFrame() {
    const frame = document.getElementById("owner-reps-panel")
    if (frame && typeof frame.reload === "function") frame.reload()
  }

  _repText(key, vars = {}) {
    const i18n = this.ownerRepI18nValue || {}
    const fallbacks = {
      remove: "This takes %{name} off all of your projects and puts them in the trash, where you can restore them. Their account stays with their own company.",
      trashIntro: "People taken off your projects from the Owner's Reps tab. Restoring someone puts them back on the projects they were on.",
      trashEmpty: "Nobody has been removed from your Owner's Reps."
    }
    let text = i18n[key] || fallbacks[key] || ""
    Object.entries(vars).forEach(([k, v]) => { text = text.split(`%{${k}}`).join(v) })
    return text
  }

  _repRemoveText(name) {
    return this._repText("remove", { name: (name || "").trim() || "this person" })
  }

  _setConfirmText(text) {
    const p = this.confirmModalTarget.querySelector("p")
    if (!p) return
    if (this._confirmDefaultText === undefined) this._confirmDefaultText = p.textContent
    p.textContent = text || this._confirmDefaultText
  }

  // A rep person's Edit pop-up: no Admin Assistant, no Regional (the regions
  // are this company's), no moving them between accounts.
  _paintOwnerRepMode() {
    const rep = !!this._ownerRepId
    if (this.hasPermAdminAssistantTarget) this.permAdminAssistantTarget.closest("div").classList.toggle("hidden", rep)
    const regional = this.permRoleTarget.querySelector('option[value="regional_access"]')
    if (regional) { regional.hidden = rep; regional.disabled = rep }
    if (rep && this.hasSwitchCompanySectionTarget) this.switchCompanySectionTarget.classList.add("hidden")
  }

  _paintTrashMode() {
    const modal = this.removedUsersModalTarget
    const rep = !!this._trashRepMode
    modal.querySelectorAll("[data-team-trash-only]").forEach(el => el.classList.toggle("hidden", rep))
    const intro = modal.querySelector("[data-team-trash-intro]")
    if (intro) {
      if (intro.dataset.defaultText === undefined) intro.dataset.defaultText = intro.textContent
      intro.textContent = rep ? this._repText("trashIntro") : intro.dataset.defaultText
    }
    const empty = this.removedUsersEmptyTarget
    if (empty.dataset.defaultText === undefined) empty.dataset.defaultText = empty.textContent
    empty.textContent = rep ? this._repText("trashEmpty") : empty.dataset.defaultText
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  // ─── Removed users (trash) ─────────────────────────────────────────

  async openRemovedUsers() {
    this._trashRepMode = this._ownerRepsTabActive()
    this._paintTrashMode()
    this.removedUsersModalTarget.classList.remove("hidden")
    this.removedUsersEmptyTarget.classList.add("hidden")
    this.removedUsersLoadingTarget.classList.remove("hidden")
    this._showRemovedUsersHistory(false)
    this._removedUsersBody().innerHTML = ""

    try {
      const listUrl = this._trashRepMode ? this.ownerRepRemovedUrlValue : this.deletedUsersUrlValue
      const resp = await fetch(listUrl, { headers: { Accept: "application/json" } })
      if (!resp.ok) throw new Error("failed")
      const data = await resp.json()
      this._removedUsers = data.users || []
      this._renderRemovedUsers(this._removedUsers)
    } catch {
      this._showToast("Could not load removed users", "error")
    } finally {
      this.removedUsersLoadingTarget.classList.add("hidden")
    }
  }

  closeRemovedUsers() {
    this.removedUsersModalTarget.classList.add("hidden")
  }

  // ── Trash history (every add / remove / restore, by whom, when) ────

  // The header clock icon: open the modal straight onto the history view.
  openRemovedUsersHistoryDirect() {
    this.openRemovedUsers()
    this._showRemovedUsersHistory(true)
    this._loadRemovedUsersHistory()
  }

  toggleRemovedUsersHistory(event) {
    event.preventDefault()
    const showingHistory = !this.removedUsersHistorySectionTarget.classList.contains("hidden")
    this._showRemovedUsersHistory(!showingHistory)
    if (!showingHistory) this._loadRemovedUsersHistory()
  }

  _showRemovedUsersHistory(show) {
    if (!this.hasRemovedUsersHistorySectionTarget) return
    this.removedUsersHistorySectionTarget.classList.toggle("hidden", !show)
    this.removedUsersListSectionTarget.classList.toggle("hidden", show)
    const link = this.removedUsersHistoryToggleTarget
    link.textContent = show ? link.dataset.backLabel : link.dataset.showLabel
  }

  async _loadRemovedUsersHistory() {
    const body = this.removedUsersModalTarget.querySelector(".js-removed-users-history")
    body.innerHTML = ""
    this.removedUsersHistoryEmptyTarget.classList.add("hidden")
    this.removedUsersHistoryLoadingTarget.classList.remove("hidden")

    try {
      const resp = await fetch(this.teamMemberHistoryUrlValue, { headers: { Accept: "application/json" } })
      if (!resp.ok) throw new Error("failed")
      const data = await resp.json()
      const events = data.events || []
      this.removedUsersHistoryEmptyTarget.classList.toggle("hidden", events.length > 0)

      const chips = {
        added:    ["bg-blue-50 text-blue-700", "Added"],
        removed:  ["bg-red-50 text-red-700", "Removed"],
        restored: ["bg-green-50 text-green-700", "Restored"]
      }
      events.forEach(e => {
        const [chipClass, chipLabel] = chips[e.action] || ["bg-gray-100 text-gray-600", e.action]
        const tr = document.createElement("tr")
        tr.className = "bg-white"
        tr.innerHTML = `
          <td class="px-2 py-1.5 text-gray-500 whitespace-nowrap">${this._escapeHtml(e.on)}</td>
          <td class="px-2 py-1.5">
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium ${chipClass}">${chipLabel}</span>
          </td>
          <td class="px-2 py-1.5 text-gray-900">${this._escapeHtml(e.user)}</td>
          <td class="px-2 py-1.5 text-gray-600">${this._escapeHtml(e.actor)}</td>
        `
        body.appendChild(tr)
      })
    } catch {
      this._showToast("Could not load history", "error")
    } finally {
      this.removedUsersHistoryLoadingTarget.classList.add("hidden")
    }
  }

  searchRemovedUsers(event) {
    const terms = event.currentTarget.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
    this._renderRemovedUsers((this._removedUsers || []).filter(user => {
      const haystack = [user.name, user.email, user.role, user.removed_on, user.removed_by].filter(Boolean).join(" ").toLowerCase()
      return terms.every(term => haystack.includes(term))
    }))
  }

  _removedUsersBody() {
    return this.removedUsersModalTarget.querySelector(".js-removed-users")
  }

  _renderRemovedUsers(users) {
    const body = this._removedUsersBody()
    body.innerHTML = ""
    this.removedUsersEmptyTarget.classList.toggle("hidden", users.length > 0)

    users.forEach(user => {
      const tr = document.createElement("tr")
      tr.className = "bg-white"
      tr.innerHTML = `
        <td class="px-2 py-1.5">
          <div class="text-gray-900">${this._escapeHtml(user.name)}</div>
          <div class="text-gray-400">${this._escapeHtml(user.email)}</div>
        </td>
        <td class="hidden sm:table-cell px-2 py-1.5 text-gray-600">${this._escapeHtml(user.role)}</td>
        <td class="px-2 py-1.5 text-center text-gray-600">${this._escapeHtml(user.removed_on || "—")}</td>
        <td class="hidden sm:table-cell px-2 py-1.5 text-gray-600">${this._escapeHtml(user.removed_by || "—")}</td>
        <td class="px-2 py-1.5 text-right"></td>
      `
      const button = document.createElement("button")
      button.type = "button"
      button.className = "px-3 py-1 text-[13px] text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer"
      button.textContent = "Restore"
      button.addEventListener("click", () => this._restoreRemovedUser(user, tr, button))
      tr.lastElementChild.appendChild(button)
      body.appendChild(tr)
    })
  }

  // No role is sent: the server puts them back with the one they had when
  // they were removed.
  async _restoreRemovedUser(user, row, button) {
    button.disabled = true
    button.innerHTML = '<span class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>'

    try {
      const repMode = !!user.rep_company_id
      const resp = await fetch(repMode ? this._repUrl(this.ownerRepRestoreUrlValue, user.rep_company_id, user.id) : this.restoreUserUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        body: JSON.stringify({ email: user.email })
      })
      if (!resp.ok) throw new Error("failed")

      const data = await resp.json()
      row.remove()
      this._removedUsers = (this._removedUsers || []).filter(candidate => !(candidate.id === user.id && candidate.rep_company_id === user.rep_company_id))
      this.removedUsersEmptyTarget.classList.toggle("hidden", this._removedUsersBody().children.length > 0)
      if (repMode) {
        this._reloadOwnerRepsFrame()
      } else {
        this._reloadFrame()
        this._updateUserCount(1)
      }
      this._showToast(data.message || "User restored successfully")
    } catch {
      button.disabled = false
      button.textContent = "Restore"
      this._showToast("Could not restore that user", "error")
    }
  }

  _updateUserCount(delta) {
    if (!this.hasUserCountTarget) return
    const el = this.userCountTarget
    el.textContent = Math.max(0, parseInt(el.textContent) + delta)
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── "User permissions": invited-only vs all company projects ──────────
  // (Phelipe 2026-08-20). Opens from the row kebab; server enforces the
  // same allowance ladder as removal.
  stopPropagation(e) { e.stopPropagation() }

  openAccessScopeModal(e) {
    const btn = e.currentTarget
    this._accessScopeUserId = btn.dataset.userId
    if (this.hasAccessScopeNameTarget) this.accessScopeNameTarget.textContent = btn.dataset.userName || ""
    const scope = btn.dataset.userScope || "invited"
    this.accessScopeModalTarget.querySelectorAll('input[name="access_scope"]').forEach((r) => {
      r.checked = r.value === scope
    })
    this._paintAccessScopeChoice()
    this.accessScopeModalTarget.querySelectorAll('input[name="access_scope"]').forEach((r) => {
      r.onchange = () => this._paintAccessScopeChoice()
    })
    this.accessScopeModalTarget.classList.remove("hidden")
  }

  closeAccessScopeModal() {
    if (this.hasAccessScopeModalTarget) this.accessScopeModalTarget.classList.add("hidden")
  }

  _paintAccessScopeChoice() {
    this.accessScopeModalTarget.querySelectorAll("[data-access-scope-option]").forEach((label) => {
      const on = label.querySelector("input").checked
      label.classList.toggle("border-brand", on)
      label.classList.toggle("bg-brand/5", on)
      label.classList.toggle("border-gray-200", !on)
    })
  }

  // ── "Page access" (Phelipe 2026-08-24): which pages a teammate can
  // open, across all their projects. Empty box = that page is hidden and
  // its URLs bounce, enforced server-side. ──
  openPageAccessModal(event) {
    const d = event.currentTarget.dataset
    this.pageAccessUserIdTarget.value = d.userId
    this.pageAccessNameTarget.textContent = d.userName || ""
    const picked = (d.userSections || "").split(",").filter(Boolean)
    // No saved restriction = everything allowed.
    this.pageAccessCheckboxTargets.forEach(cb => {
      cb.checked = picked.length === 0 || picked.includes(cb.value)
    })
    this.pageAccessErrorTarget.classList.add("hidden")
    this.pageAccessModalTarget.classList.remove("hidden")
  }

  closePageAccessModal() {
    if (this.hasPageAccessModalTarget) this.pageAccessModalTarget.classList.add("hidden")
  }

  // ── "Info": what the team should know about a teammate ────────────────
  // (Phelipe 2026-09-09). Read-only card fed by /users/:id/team_info; times
  // arrive as ISO strings and are shown in the viewer's own time zone.
  async openInfoModal(e) {
    const userId = e.currentTarget.dataset.userId
    const repId = e.currentTarget.dataset.ownerRepId
    if (!userId) return
    this.infoBodyTarget.innerHTML = ""
    this.infoLoadingTarget.classList.remove("hidden")
    this.infoModalTarget.classList.remove("hidden")
    try {
      const url = repId ? this._repUrl(this.ownerRepInfoUrlValue, repId, userId) : this.teamInfoUrlValue.replace(":id", userId)
      const resp = await fetch(url, { headers: { Accept: "application/json" } })
      if (!resp.ok) throw new Error("failed")
      const info = await resp.json()
      this.infoBodyTarget.innerHTML = this._infoHtml(info)
      track("Team Member Info Viewed", {
        target_user_id: Number(userId),
        viewer_role: this.isCommanderValue ? "commander" : (this.isGroupAdminValue ? "group_admin" : "member"),
        target_status: info.account?.status
      })
    } catch {
      this.closeInfoModal()
      this._showToast("Could not load this user's info", "error")
    } finally {
      this.infoLoadingTarget.classList.add("hidden")
    }
  }

  closeInfoModal() {
    if (this.hasInfoModalTarget) this.infoModalTarget.classList.add("hidden")
  }

  _infoHtml(info) {
    const esc = (v) => this._escapeHtml(v == null ? "" : String(v))
    const u = info.user || {}, acct = info.account || {}, si = info.sign_ins || {}
    const mob = info.mobile || {}
    const acc = info.access || {}, hist = info.history || []
    const pill = (label, cls) => `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium ${cls}">${esc(label)}</span>`
    const titleize = (str) => String(str).replace(/\b\w/g, (c) => c.toUpperCase())

    const avatar = u.avatar_url
      ? `<img src="${esc(u.avatar_url)}" class="w-12 h-12 rounded-full object-cover shrink-0" alt="">`
      : `<div class="w-12 h-12 rounded-full bg-brand/10 text-brand flex items-center justify-center text-[15px] font-semibold shrink-0">${esc(u.initials)}</div>`

    const pills = [pill(u.role || "User", "bg-gray-100 text-gray-600")]
    if (u.read_only) pills.push(pill("Read-only", "bg-gray-100 text-gray-500"))
    if (u.group_admin) pills.push(pill("Group Admin", "bg-indigo-100 text-indigo-700"))
    pills.push(acct.status === "never_signed_in"
      ? pill("Hasn't signed in yet", "bg-amber-100 text-amber-700")
      : pill("Active", "bg-green-100 text-green-700"))

    const neverSignedIn = acct.status === "never_signed_in"
    const resendBtn = neverSignedIn
      ? `<button type="button"
                 class="inline-flex items-center gap-1.5 px-2.5 py-1 text-[13px] font-medium text-brand bg-brand/5 border border-brand/20 rounded-lg hover:bg-brand/10 cursor-pointer whitespace-nowrap"
                 data-action="click->team#resendInvite" data-user-id="${esc(u.id)}" data-user-email="${esc(u.email)}">Resend invite</button>`
      : ""

    const tile = (label, value, sub) => `
      <div class="rounded-lg bg-gray-50 px-3 py-2">
        <div class="text-[13px] text-gray-500">${esc(label)}</div>
        <div class="text-[13px] font-medium text-gray-900">${esc(value)}</div>
        ${sub ? `<div class="text-[13px] text-gray-500">${esc(sub)}</div>` : ""}
      </div>`
    const lastSignIn = si.last_at ? this._fmtDate(si.last_at, true) : "Never"
    const tiles = [
      neverSignedIn
        ? tile("Added", this._fmtDate(acct.signed_up_at) || "—", this._ago(acct.signed_up_at))
        : tile("Signed up", this._fmtDate(acct.invitation_accepted_at || acct.signed_up_at) || "—", this._ago(acct.invitation_accepted_at || acct.signed_up_at)),
      tile("Last sign-in", lastSignIn, si.last_at ? this._ago(si.last_at) : (acct.invitation_sent_at ? `Invited ${this._fmtDate(acct.invitation_sent_at)}` : "")),
      tile("Last active", si.last_active_at ? this._fmtDate(si.last_active_at) : "—", this._ago(si.last_active_at)),
      tile("Sign-ins", Number(si.count || 0).toLocaleString(), si.previous_at ? `Before that: ${this._fmtDate(si.previous_at, true)}` : "")
    ].join("")

    const row = (label, valueHtml) => `
      <div class="flex gap-3 py-2 border-t border-gray-100">
        <div class="w-28 shrink-0 text-[13px] text-gray-500">${esc(label)}</div>
        <div class="flex-1 min-w-0 text-[13px] text-gray-800">${valueHtml}</div>
      </div>`
    const rows = []

    if (acct.invited_by) {
      let v = esc(acct.invited_by)
      if (acct.invitation_sent_at) v += ` <span class="text-gray-500">· sent ${esc(this._fmtDate(acct.invitation_sent_at))}</span>`
      if (acct.invitation_accepted_at) v += ` <span class="text-gray-500">· accepted ${esc(this._fmtDate(acct.invitation_accepted_at))}</span>`
      rows.push(row("Invited by", v))
    }

    let mobileHtml
    if (mob.last_used_on) {
      mobileHtml = `${esc(mob.os || "Mobile app")} <span class="text-gray-500">· last used ${esc(this._fmtDate(mob.last_used_on))} (${esc(this._ago(mob.last_used_on))}) · ${esc(mob.days_last_30)} of the last 30 days</span>`
    } else if (mob.os) {
      mobileHtml = `${esc(mob.os)} <span class="text-gray-500">· installed, no recent use recorded</span>`
    } else {
      mobileHtml = `<span class="text-gray-500">Not using the mobile app</span>`
    }
    rows.push(row("Mobile app", mobileHtml))

    // Projects + Activity rows hidden for now (Phelipe 2026-09-09); the
    // endpoint still returns them.

    const accessBits = [acc.scope === "all_projects" ? "All company projects" : "Invited projects only"]
    accessBits.push(acc.pages && acc.pages.length ? `Pages: ${acc.pages.map(titleize).join(", ")}` : "All pages")
    if (acc.hidden) accessBits.push("Hidden from project teams")
    if (acc.muted) accessBits.push("Project emails muted")
    rows.push(row("Access", accessBits.map(esc).join(" · ")))

    if (hist.length) {
      const chips = {
        added:    ["bg-blue-50 text-blue-700", "Added"],
        removed:  ["bg-red-50 text-red-700", "Removed"],
        restored: ["bg-green-50 text-green-700", "Restored"]
      }
      const items = hist.map((h) => {
        const [cls, label] = chips[h.action] || ["bg-gray-100 text-gray-600", h.action]
        return `<div class="flex items-center gap-2 py-0.5">${pill(label, cls)}<span class="text-gray-600">${h.by ? `by ${esc(h.by)} · ` : ""}${esc(this._fmtDate(h.at))}</span></div>`
      }).join("")
      rows.push(row("Team history", items))
    }

    return `
      <div class="flex items-center gap-3 mb-3">
        ${avatar}
        <div class="min-w-0">
          <div class="text-[15px] font-semibold text-gray-900 truncate">${esc(u.name || u.email)}</div>
          <div class="text-[13px] text-gray-500 truncate">${esc(u.email)}${u.work_phone ? ` · ${esc(u.work_phone)}` : ""}</div>
          <div class="flex flex-wrap gap-1 mt-1">${pills.join("")}</div>
        </div>
        ${resendBtn ? `<div class="ml-auto shrink-0 self-start">${resendBtn}</div>` : ""}
      </div>
      <div class="grid grid-cols-2 gap-2 mb-3">${tiles}</div>
      <div>${rows.join("")}</div>`
  }

  // "2026-09-05" (a date) and "2026-09-05T14:03:00Z" (a moment) both come
  // through here; bare dates are built locally so they don't slip a day.
  _fmtDate(iso, withTime = false) {
    if (!iso) return null
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    const opts = { month: "short", day: "numeric", year: "numeric" }
    if (withTime && !m) Object.assign(opts, { hour: "numeric", minute: "2-digit" })
    return d.toLocaleString(undefined, opts)
  }

  _ago(iso) {
    if (!iso) return ""
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso)
    if (Number.isNaN(d.getTime())) return ""
    const days = Math.floor((Date.now() - d.getTime()) / 86400000)
    if (days <= 0) return "today"
    if (days === 1) return "yesterday"
    if (days < 30) return `${days} days ago`
    if (days < 365) { const months = Math.floor(days / 30); return `${months} month${months === 1 ? "" : "s"} ago` }
    const years = Math.floor(days / 365)
    return `${years} year${years === 1 ? "" : "s"} ago`
  }

  async savePageAccess() {
    const userId = this.pageAccessUserIdTarget.value
    if (!userId) return
    const sections = this.pageAccessCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value)
    if (sections.length === 0) {
      this.pageAccessErrorTarget.textContent = "Select at least one page, or remove the user instead."
      this.pageAccessErrorTarget.classList.remove("hidden")
      return
    }

    this.pageAccessSaveBtnTarget.disabled = true
    try {
      const url = this.sectionAccessUrlValue.replace(":id", userId)
      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({ construction_sections: sections.join(",") })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not update page access")
      this.closePageAccessModal()
      this._showToast(data.full_access
        ? "Page access updated — full access"
        : `Page access updated — ${sections.length} page${sections.length === 1 ? "" : "s"}`)
      this._reloadFrame()
    } catch (err) {
      this.pageAccessErrorTarget.textContent = err.message
      this.pageAccessErrorTarget.classList.remove("hidden")
    } finally {
      this.pageAccessSaveBtnTarget.disabled = false
    }
  }

  async saveAccessScope() {
    const picked = this.accessScopeModalTarget.querySelector('input[name="access_scope"]:checked')
    if (!picked || !this._accessScopeUserId) return
    this.accessScopeSaveBtnTarget.disabled = true
    try {
      const resp = await fetch(`/users/${this._accessScopeUserId}/update_access_scope`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        body: JSON.stringify({ scope: picked.value })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not update permissions")
      this.closeAccessScopeModal()
      const added = data.added || 0
      const removed = data.removed || 0
      this._showToast(data.scope === "all_projects"
        ? `Access updated — added to ${added} project${added === 1 ? "" : "s"}`
        : `Access updated — back to invited projects only${removed ? ` (left ${removed})` : ""}`)
      // The badge and the kebab's remembered choice update without a reload.
      this._reloadFrame()
    } catch (err) {
      this._showToast(err.message, "error")
    } finally {
      this.accessScopeSaveBtnTarget.disabled = false
    }
  }
}
